import StatusTag from 'components/ui/base/status/StatusTag';
import { FC } from 'react';
import { DeclarationStatuses, getDeclarationStatusLabel } from 'store/declarations/enums/common/declaration-status';
import { colors } from 'theme';

interface Props {
    status: DeclarationStatuses;
    multiple?: boolean;
    dataTestId?: string;
    small?: boolean;
    hideText?: boolean;
}
const gvmsSpecificDeclarationStatusColors = {
    [DeclarationStatuses.EMBARKED]: colors.darkGreen,
    [DeclarationStatuses.CHECKED_IN]: colors.lightGreen2,
    [DeclarationStatuses.OPEN]: colors.purpleBlue,
    [DeclarationStatuses.FINALISED]: colors.lightBlue,
    [DeclarationStatuses.NOT_FINALISABLE]: colors.lightRed,
};

const cdsSpecificDeclarationStatusColors = {
    [DeclarationStatuses.MUCR_SOE_0]: colors.lightBlue,
    [DeclarationStatuses.DECLARATION_VALIDATION]: colors.green,
    [DeclarationStatuses.GOODS_RELEASED]: colors.polarGreen,
    [DeclarationStatuses.DECLARATION_CLEARED]: colors.lightYellow,
    [DeclarationStatuses.DECLARATION_INVALIDATION]: colors.red,
    [DeclarationStatuses.DECLARATION_REJECTED]: colors.lightRed,
    [DeclarationStatuses.RE_ARRIVED_DECLARATION_IS_SEIZED]: colors.orange,
    [DeclarationStatuses.ADVANCE_DECLARATION_REGISTRATION]: colors.yellow,
    [DeclarationStatuses.DECLARATION_ACCEPTANCE]: colors.lightGreen,
    [DeclarationStatuses.DECLARATION_ACCEPTED_AT_GOODS_ARRIVAL]: colors.lightGreen3,
    [DeclarationStatuses.DECLARATION_REJECTED_AT_ARRIVAL]: colors.purpleBlue,
    [DeclarationStatuses.DECLARATION_CORRECTED]: colors.lightOrange,
    [DeclarationStatuses.DECLARATION_SUPPLEMENTED]: colors.lightGrey,
    [DeclarationStatuses.DECLARATION_RISKED]: colors.darkOrange,
    [DeclarationStatuses.CUSTOMS_POSITION_DETERMINED]: colors.blueLink,
    [DeclarationStatuses.DECLARATION_CLEARED_AFTER_GOODS_RELEASE]: colors.grey,
    [DeclarationStatuses.DECLARATION_RECEIVED]: colors.darkGrey,
    [DeclarationStatuses.GOODS_EXIT_RESULTS_RECEIVED]: colors.darkBlack2,
    [DeclarationStatuses.COMPLETED_SUCCESSFULLY]: colors.success,
    [DeclarationStatuses.MANUAL_TASK_RAISED]: colors.info,
    [DeclarationStatuses.DEPARTED]: colors.darkGreen,
    [DeclarationStatuses.FRUSTRATED]: colors.error,
    [DeclarationStatuses.DECLARATION_UNDER_RE_ARRIVALS_RISK]: colors.warning,
};

// Every ICS2-specific status is namespaced with an `ICS2_` prefix, so unlike the other modules these
// keys no longer collide with the base statuses and each one needs its own colour — including the
// statuses that previously fell through to the identically named base entries.
const ics2SpecificDeclarationStatusColors = {
    [DeclarationStatuses.ICS2_RECEIVED]: colors.darkGrey,
    [DeclarationStatuses.ICS2_REGISTERED]: colors.lightGreen2,
    [DeclarationStatuses.ICS2_REJECTED]: colors.lightRed,
    [DeclarationStatuses.ICS2_ACCEPTED]: colors.lightGreen2,
    [DeclarationStatuses.ICS2_PENDING_LINKING]: colors.periwinkleCrayola,
    [DeclarationStatuses.ICS2_LINKED]: colors.lightGreen3,
    [DeclarationStatuses.ICS2_INVALIDATED]: colors.red,
    [DeclarationStatuses.ICS2_PENDING_COMPLETENESS]: colors.periwinkleCrayola,
    [DeclarationStatuses.ICS2_COMPLETE]: colors.polarGreen,
    [DeclarationStatuses.ICS2_CONSIDERED_COMPLETE_FOR_RA]: colors.purpleBlue,
    [DeclarationStatuses.ICS2_READY_FOR_FULL_RA]: colors.lightBlue,
    [DeclarationStatuses.ICS2_COMPLETE_NO_RISK]: colors.lightGreenA70,
    [DeclarationStatuses.ICS2_COMPLETE_RISK_IDENTIFIED]: colors.lightRed,
    [DeclarationStatuses.ICS2_COMPLETE_DNL]: colors.error,
    [DeclarationStatuses.ICS2_PARTIALLY_ARRIVED]: colors.lightGrey2,
    [DeclarationStatuses.ICS2_ARRIVED]: colors.black,
    [DeclarationStatuses.ICS2_PRESENTED]: colors.lightGrey5,
    [DeclarationStatuses.ICS2_RELEASED]: colors.darkGreen,
    [DeclarationStatuses.ICS2_NOT_RELEASED]: colors.lightPink,
    [DeclarationStatuses.ICS2_ASSESSMENT_COMPLETED]: colors.darkGreen,
    [DeclarationStatuses.ICS2_UNDER_CONTROL]: colors.darkOrange,
    [DeclarationStatuses.ICS2_AMENDED]: colors.lightYellow,
};

const aisSpecificDeclarationStatusColors = {
    [DeclarationStatuses.AWAITING_SUPPLEMENTARY]: colors.periwinkleCrayola,
};

const aesSpecificDeclarationStatusColors = {
    [DeclarationStatuses.DIVERSION_REJECTED]: colors.lightGreen3,
    [DeclarationStatuses.REGISTERED_AND_WAITING_FOR_PRESENTATION_OF_GOODS]: colors.lightBlue,
    [DeclarationStatuses.GOODS_RELEASED_FOR_IMMEDIATE_LEAVE]: colors.lightGreen,
    [DeclarationStatuses.GOODS_PRESENTED_AT_EXIT]: colors.lightGreen,
    [DeclarationStatuses.EXPORTED]: colors.green,
    [DeclarationStatuses.EXPORTED_STOPPED_AT_EXIT]: colors.orange,
    [DeclarationStatuses.CANCELLED]: colors.lightRed,
    [DeclarationStatuses.INVALIDATED]: colors.red,
    [DeclarationStatuses.GOODS_NOT_ALLOWED_TO_EXIT]: colors.purpleBlueA15,
    [DeclarationStatuses.NOT_RELEASED_FOR_EXPORT]: colors.purpleBlue,
    [DeclarationStatuses.GOODS_RELEASED_FOR_EXPORT]: colors.polarGreen,
    [DeclarationStatuses.GOODS_READY_TO_BE_RELEASED]: colors.yellow,
    [DeclarationStatuses.EXITED]: colors.lightBlue,
    [DeclarationStatuses.EXPORT_STOPPED_DISCREPANCIES_AT_EXIT]: colors.lightRed,
};

const nctsSpecificDeclarationStatusColors = {
    [DeclarationStatuses.MOVEMENT_RELEASED]: colors.darkGreen,
    [DeclarationStatuses.AAR_CREATED]: colors.lightGreen,
    [DeclarationStatuses.ARRIVAL_ACCEPTED]: colors.lightGreen,
    [DeclarationStatuses.UNLOADING]: colors.lightRed,
    [DeclarationStatuses.UNLOADING_REMARKS]: colors.lightRed,
    [DeclarationStatuses.GUARANTEE_UNDER_AMENDMENT]: colors.lightOrange,
    [DeclarationStatuses.GUARANTEE_UNDER_REGISTRATION]: colors.lightOrange,
    [DeclarationStatuses.IDLE]: colors.orange,
    [DeclarationStatuses.NOT_RELEASED_FOR_TRANSIT]: colors.red,
    [DeclarationStatuses.UNDER_AMENDMENT_REQUEST_DUE_TO_EFBT]: colors.orange,
    [DeclarationStatuses.INCIDENT_REGISTERED]: colors.red,
    [DeclarationStatuses.MOVEMENT_WRITTEN_OFF]: colors.red,
    [DeclarationStatuses.UNDER_RECOVERY_PROCEDURE]: colors.lightOrange,
    [DeclarationStatuses.PRE_LODGED]: colors.lightGreen,
    [DeclarationStatuses.MOVEMENT_UNDER_RESOLUTION]: colors.yellow,
    [DeclarationStatuses.UNDER_RELEASE_REQUEST]: colors.lightYellow,
    [DeclarationStatuses.UNDER_REVIEW]: colors.lightOrange,
};

export const declarationStatusColor = {
    [DeclarationStatuses.DRAFT]: colors.grey,
    [DeclarationStatuses.SUBMITTED]: colors.lightGreenA70,
    [DeclarationStatuses.REJECTED]: colors.lightRed,
    [DeclarationStatuses.INVALID]: colors.lightRed,
    [DeclarationStatuses.AWAITING_TARIFF_CALCULATION]: colors.lightOrange,
    [DeclarationStatuses.ACCEPTED]: colors.lightGreen2,
    [DeclarationStatuses.AWAITING_RISK]: colors.lightOrange,
    [DeclarationStatuses.UNDER_PAYMENT]: colors.lightOrange1,
    [DeclarationStatuses.INSUFFICIENT_FUNDS]: colors.pink,
    [DeclarationStatuses.UNDER_CONTROL]: colors.darkOrange,
    [DeclarationStatuses.RELEASED]: colors.darkGreen,
    [DeclarationStatuses.REGISTERED]: colors.lightGreenA70,
    [DeclarationStatuses.NOT_RELEASED]: colors.lightPink,
    [DeclarationStatuses.UPLOAD_DOCUMENTS]: colors.grey,
    [DeclarationStatuses.INVALIDATE]: colors.black,
    [DeclarationStatuses.RISKED]: colors.darkOrange,
    [DeclarationStatuses.PENDING]: colors.lightOrange,
    ...aesSpecificDeclarationStatusColors,
    ...aisSpecificDeclarationStatusColors,
    ...cdsSpecificDeclarationStatusColors,
    ...gvmsSpecificDeclarationStatusColors,
    ...nctsSpecificDeclarationStatusColors,
    ...ics2SpecificDeclarationStatusColors,
};

const DeclarationStatusTag: FC<Props> = ({ status, multiple, dataTestId, small, hideText }) => {
    const size = small ? 'small' : 'large';

    if (multiple) {
        return <StatusTag color={colors.grey} status="Multiple" size={size} hideText={hideText} />;
    }
    return (
        <StatusTag
            color={declarationStatusColor[status] ?? colors.lightGrey}
            status={getDeclarationStatusLabel(status)}
            dataTestId={dataTestId}
            hideText={hideText}
            size={size}
        />
    );
};
export default DeclarationStatusTag;
